import { createContext, useContext, type ReactNode } from 'react';

/**
 * Catalog navigation context — lets the shared public catalog components
 * (PublicAuctionList, PublicAuctionDetail) render either on the real bidder
 * site OR inside the owner's onboarding preview surface (`/preview/<slug>`,
 * Phase E1) without duplicating them.
 *
 * - `basePath`: prefix for catalog-internal links. `''` on the real site
 *   (links stay `/auctions/...`, byte-identical to before); `/preview/<slug>`
 *   inside the preview subtree so navigation stays contained there.
 * - `previewMode`: true only in the preview frame. The preview covers the
 *   storefront browse (list → auction detail with its lot grid) but NOT the
 *   live-bidding surfaces (lot detail / live room), so in preview the lot cards
 *   render display-only and the "Live room" link is hidden — no dead links,
 *   and zero changes to the bidding code paths.
 *
 * Default (no provider) = real-site behavior, so components work unchanged
 * outside a preview.
 */
export interface CatalogNavValue {
  basePath: string;
  previewMode: boolean;
}

const DEFAULT: CatalogNavValue = { basePath: '', previewMode: false };

const CatalogNavContext = createContext<CatalogNavValue>(DEFAULT);

export function CatalogNavProvider({
  value,
  children,
}: {
  value: CatalogNavValue;
  children: ReactNode;
}) {
  return <CatalogNavContext.Provider value={value}>{children}</CatalogNavContext.Provider>;
}

export function useCatalogNav(): CatalogNavValue {
  return useContext(CatalogNavContext);
}
